import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.getElementById('root')?.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf8f6] to-[#f0ebe5] text-[#3d2c24]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-[#5c4033]/10 px-4 py-3 pt-safe flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2.5 rounded-full hover:bg-stone-200/60 active:bg-stone-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-[#5c4033]" />
        </button>
        <h1 className="text-lg font-semibold text-[#5c4033]" style={{ fontFamily: "'Playfair Display', serif" }}>Privacy Policy</h1>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-5 py-8 pb-safe space-y-6 leading-relaxed text-base">
        <div>
          <h1 className="text-3xl font-bold text-[#5c4033] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Privacy Policy</h1>
          <p className="text-[#8b7355] text-sm">Last updated: March 1, 2026</p>
        </div>

        <p>This Privacy Policy explains how fashionDisc ("we", "us", or "our") collects, uses, shares, and protects personal data when you use the fashionDisc mobile application and related services (collectively, the "Service").</p>
        <p>By using the Service, you acknowledge that you have read and understood this Privacy Policy.</p>

        <Section title="1. Data Controller">
          <p>For the purposes of the EU General Data Protection Regulation (GDPR), the data controller is:</p>
          <p><strong>fashionDisc</strong><br />Based in Cyprus (European Union)<br />Email: <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500] underline">fashiondisc25@gmail.com</a></p>
          <p><strong>Supervisory Authority:</strong><br />Office of the Commissioner for Personal Data Protection (Cyprus)</p>
          <p>You also have the right to lodge a complaint with your local data protection authority in the EU.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p className="font-semibold">a) Account Information</p>
          <p>When you create an account, we collect:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Name and email address</li>
            <li>Password (stored securely using bcrypt hashing — never in plain text)</li>
            <li>Authentication tokens for session management</li>
          </ul>
          <p>If you sign in using Google or Apple, we receive your name and email address from those services. We do not receive or store your Google or Apple passwords.</p>

          <p className="font-semibold mt-4">b) Photos and Images</p>
          <p>You may:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Upload photos of clothing items</li>
            <li>Take photos using your device camera</li>
            <li>Upload images for virtual try-on features</li>
          </ul>
          <p>Images are stored securely using Cloudinary and linked to your account. You may delete images at any time.</p>

          <p className="font-semibold mt-4">c) Style Preferences and User Content</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Style preferences and fashion interests</li>
            <li>Saved items, capsule wardrobes, and outfits</li>
            <li>Calendar entries and packing lists</li>
            <li>Interactions with AI-powered styling features</li>
          </ul>

          <p className="font-semibold mt-4">d) Payment Information</p>
          <p>Payments are processed by Apple (for iOS in-app purchases) or Stripe (for web). We do not store full card details. Payment providers share only subscription status and limited payment identifiers with us.</p>

          <p className="font-semibold mt-4">e) Usage Data</p>
          <p>We automatically collect:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Device type and operating system</li>
            <li>App version</li>
            <li>Features used and actions taken</li>
            <li>Date and time of access</li>
            <li>Error logs for troubleshooting</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use personal data to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Provide and operate the Service</li>
            <li>Create and manage accounts</li>
            <li>Process subscriptions</li>
            <li>Deliver AI-powered styling and wardrobe features</li>
            <li>Send transactional emails (e.g. verification, password resets)</li>
            <li>Respond to support requests</li>
            <li>Improve and develop the Service</li>
            <li>Prevent fraud, abuse, and unauthorised access</li>
          </ul>
        </Section>

        <Section title="4. AI-Powered Features">
          <p>FashionDisc uses AI-powered tools to analyse images you upload in order to provide features such as digital wardrobe organisation, item categorisation, and personalised outfit recommendations.</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Uploaded images are processed automatically to identify clothing items and attributes</li>
            <li>The AI does not perform biometric identification, facial recognition, or attempt to uniquely identify individuals</li>
            <li>AI processing is not used to make legal or similarly significant decisions about you</li>
            <li>Images and user inputs are not used to train AI models by us or by third-party providers unless you have explicitly consented</li>
          </ul>
          <p>You remain in control of your content and may delete uploaded images at any time.</p>
        </Section>

        <Section title="5. Legal Basis for Processing Personal Data">
          <p>We process personal data in accordance with Article 6 GDPR on the following legal bases:</p>

          <p className="font-semibold mt-2">a) Performance of a Contract</p>
          <p>Processing necessary to provide the Service, including:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Account creation and management</li>
            <li>Digital wardrobe maintenance</li>
            <li>AI-powered features</li>
            <li>Subscription processing</li>
            <li>Customer support</li>
          </ul>

          <p className="font-semibold mt-2">b) Consent</p>
          <p>Where required by law, including:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Marketing communications</li>
            <li>Optional, non-essential features</li>
            <li>Certain cookies or similar technologies</li>
          </ul>
          <p>You may withdraw consent at any time.</p>

          <p className="font-semibold mt-2">c) Legitimate Interests</p>
          <p>Including:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Improving and developing the Service</li>
            <li>Ensuring platform security</li>
            <li>Preventing fraud and misuse</li>
          </ul>
          <p>Only where these interests are not overridden by your rights and freedoms.</p>

          <p className="font-semibold mt-2">d) Legal Obligation</p>
          <p>Where processing is required by applicable law, including tax, accounting, and regulatory obligations.</p>
        </Section>

        <Section title="6. Information Sharing">
          <p><strong>We do not sell personal data.</strong></p>

          <p className="font-semibold mt-2">Service Providers</p>
          <p>We use trusted third-party service providers to help operate the Service, including for image storage, payment processing, email delivery, hosting, and authentication. These providers process data on our behalf and are contractually required to protect your information.</p>

          <p className="font-semibold mt-3">Business Transfers</p>
          <p>Personal data may be transferred in the event of a merger, acquisition, or sale of assets. Users will be notified of material changes.</p>

          <p className="font-semibold mt-3">Legal Requirements</p>
          <p>We may disclose data where required by law or valid legal requests.</p>
        </Section>

        <Section title="7. Data Storage and Security">
          <p>We apply appropriate technical and organisational measures, including:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>bcrypt password hashing</li>
            <li>Encrypted data transmission (HTTPS)</li>
            <li>Access controls and secure infrastructure</li>
          </ul>
          <p>No system is 100% secure, but we take reasonable steps to protect your data.</p>
        </Section>

        <Section title="8. Data Retention">
          <ul className="list-disc ml-5 space-y-2">
            <li><strong>Account data:</strong> retained while your account is active</li>
            <li><strong>Images:</strong> deleted upon account deletion or earlier at your request</li>
            <li><strong>AI interaction data:</strong> not retained beyond the session unless saved</li>
            <li><strong>Payment records:</strong> retained as legally required</li>
          </ul>
        </Section>

        <Section title="9. Your Data Protection Rights">
          <p>Under GDPR, you have the right to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Access</li>
            <li>Rectification</li>
            <li>Erasure</li>
            <li>Restriction or objection</li>
            <li>Data portability</li>
            <li>Withdraw consent (where applicable)</li>
          </ul>
          <p className="font-semibold mt-2">Exercising Your Rights</p>
          <p>Contact: <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500] underline">fashiondisc25@gmail.com</a></p>
          <p>We may require identity verification. Requests that are manifestly unfounded, excessive, or repetitive may be refused or subject to a reasonable fee. We respond within one month.</p>
        </Section>

        <Section title="10. Account Deletion">
          <p>You may delete your account at any time through the app or by contacting <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500] underline">fashiondisc25@gmail.com</a>.</p>
          <p>Upon deletion:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Account and profile data are removed</li>
            <li>Images and wardrobe data are deleted</li>
            <li>Active subscriptions are cancelled</li>
          </ul>
          <p>Some data may be retained where legally required.</p>
        </Section>

        <Section title="11. International Data Transfers">
          <p>Data may be processed outside the EEA by service providers located in countries such as the United States.</p>
          <p>We rely on:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>European Commission adequacy decisions, or</li>
            <li>Standard Contractual Clauses</li>
          </ul>
          <p>Copies of applicable safeguards may be requested by contacting us.</p>
        </Section>

        <Section title="12. Children's Privacy">
          <p>The Service is not intended for children under 13 (or 16 in the EU). We do not knowingly collect data from children. If such data is identified, it will be deleted promptly.</p>
        </Section>

        <Section title="13. Cookies and Tracking">
          <p>See our Cookie Statement below.</p>
        </Section>

        <Section title="14. Changes to This Privacy Policy">
          <p>We may update this policy from time to time. Material changes will be communicated within the app. Continued use of the Service indicates acceptance.</p>
        </Section>

        <Section title="15. Contact Us">
          <p>
            <strong>fashionDisc</strong><br />
            Email: <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500] underline">fashiondisc25@gmail.com</a>
          </p>
        </Section>

        <hr className="border-t border-[#5c4033]/20 my-8" />

        <div>
          <h1 className="text-2xl font-bold text-[#5c4033] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Cookie Statement</h1>
          <p className="text-[#8b7355] text-sm">Last updated: March 1, 2026</p>
        </div>
        <p>This Cookie Statement explains how fashionDisc uses cookies and similar technologies.</p>

        <Section title="1. Mobile Application">
          <p>The fashionDisc mobile application does not use cookies.</p>
        </Section>

        <Section title="2. Website Cookies">
          <p>If you access our website, we may use strictly necessary cookies to ensure proper functionality (e.g. security, load balancing).</p>
          <p>We do not use:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Advertising cookies</li>
            <li>Cross-site tracking cookies</li>
            <li>Third-party analytics cookies</li>
          </ul>
        </Section>

        <Section title="3. Third-Party Cookies">
          <p>The app does not use third-party cookies or cross-site tracking.</p>
        </Section>

        <Section title="4. Managing Cookies">
          <p>You can manage or disable cookies through your browser settings. Disabling essential cookies may affect website functionality.</p>
        </Section>

        <Section title="5. Changes to This Cookie Statement">
          <p>We may update this statement as required. Changes will be posted on this page.</p>
        </Section>

        <div className="h-12" />
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-[#cc5500] mt-6 mb-3">{title}</h2>
      <div className="space-y-3 text-[#5c4033]">{children}</div>
    </section>
  );
}
